<script setup>
/**
 * TagMultiSelect.vue
 *
 * Reusable multi-select input for conversation tags. Fetches existing tags
 * from the API whenever `botId` becomes available or changes, renders them as
 * typeahead suggestions, and allows creating new tags inline by selecting the
 * "add" sentinel that appears in the dropdown when no exact match exists.
 *
 * New tags are persisted via `tagsService.createTag` **before** being added to
 * the emitted `modelValue`; creation failures are silently discarded.
 *
 * No i18n is used inside this component by design — the `placeholder` prop
 * must already be a translated string provided by the consumer.
 *
 * @example
 * <TagMultiSelect
 *   :workspaceId="workspaceId"
 *   :botId="botId"
 *   v-model="selectedTags"
 *   :placeholder="$t('conversations.tags.placeholder')"
 * />
 */
import { ref, watch } from 'vue'
import { listTags, createTag as apiCreateTag } from '@/services/tagsService'

const props = defineProps({
  /** Workspace that owns the tag namespace. */
  workspaceId: {
    type: String,
    required: true,
  },
  /**
   * Bot whose tag list should be fetched. May be `null` while the consumer is
   * still resolving which bot to use. When falsy the component shows an empty
   * suggestion list and silently suppresses tag creation.
   */
  botId: {
    type: String,
    default: null,
  },
  /** Currently selected tag name strings — the v-model binding target. */
  modelValue: {
    type: Array,
    default: () => [],
  },
  /**
   * Input placeholder text. Pass an already-translated string; this component
   * contains no i18n calls by design.
   */
  placeholder: {
    type: String,
    default: '',
  },
  /**
   * When `false`, this instance can only pick from the existing tag catalog —
   * it can never create a new tag. Used by read-only filter usages (e.g. the
   * Transcripts tag filter) so they can never introduce a name the catalog
   * doesn't already have. The flow-node tag editor keeps the default `true`.
   */
  creatable: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:modelValue'])

/**
 * Prefix used to mark "create new tag" sentinel entries added to
 * `filteredSuggestions`. The NUL byte ensures it cannot collide with any real
 * tag name typed by users.
 */
const CREATE_PREFIX = '\x00CREATE:'

/** Full list of tag name strings fetched from the API for the current bot. */
const options = ref([])

/** Dropdown options produced by the most recent `@complete` call. */
const filteredSuggestions = ref([])

/** Local copy of `props.modelValue`; bound as the AutoComplete's v-model. */
const internalValue = ref([...props.modelValue])

// ── External modelValue → internalValue sync ───────────────────────────────
// Keeps `internalValue` in sync when the parent updates `modelValue` (e.g. a
// tag removed from outside this component). Only syncs when the sorted content
// actually differs to avoid triggering the watcher in a loop.
watch(
  () => props.modelValue,
  (newVal) => {
    const incoming = [...newVal].sort().join('\0')
    const current = [...internalValue.value].sort().join('\0')
    if (incoming !== current) internalValue.value = [...newVal]
  },
  { deep: true },
)

// ── Fetch tags when botId is available or changes ─────────────────────────
watch(
  () => props.botId,
  async (botId) => {
    if (!botId) {
      options.value = []
      filteredSuggestions.value = []
      return
    }
    try {
      const tags = await listTags(props.workspaceId, botId)
      options.value = Array.isArray(tags) ? tags : []
    } catch {
      options.value = []
    }
  },
  { immediate: true },
)

/** Returns `true` when `option` is the "create new tag" sentinel string. */
const isCreateOption = (option) =>
  typeof option === 'string' && option.startsWith(CREATE_PREFIX)

// ── Defense-in-depth: free-text bypass guard ───────────────────────────────
// In typeahead=true mode (our default) AutoComplete's multiple mode does NOT
// push raw typed text into the model without going through onOptionSelect →
// @item-select: Enter/Tab with no dropdown option focused only closes the
// overlay, and blur never touches the model. This watcher is a safety net for
// any future PrimeVue behavioral drift or consumer environment where an item
// somehow lands in `internalValue` without passing through `onItemSelect`.
//
// Invariant 1 — no double-create for items from onItemSelect:
//   `onItemSelect` always writes the created name into `options` BEFORE writing
//   it into `internalValue`. Because Vue watchers run asynchronously (after the
//   current synchronous tick), by the time this watcher fires for a tag that
//   came through `onItemSelect`, that tag is already in `options`. The watcher
//   therefore sees `unknownItems = []` and exits early — no second createTag
//   call, no second emit.
//
// Invariant 2 — no spurious creates for parent-supplied tags:
//   `knownSet` includes `props.modelValue`, so any tag the parent has declared
//   as already-selected is excluded from `unknownItems` regardless of whether
//   `options` has been populated yet. This covers the async botId/listTags race
//   where the parent pushes pre-existing tag names before `listTags` resolves.
//
// Invariant 3 — self-terminating re-fire:
//   When this watcher resolves an unknown item, it adds the result to `options`
//   before updating `internalValue`, so its own re-fire finds `unknownItems =
//   []` and exits immediately — no infinite loop.
watch(internalValue, async (newVal) => {
  // Build the set of "known" tag names: anything already fetched from the API
  // OR already declared as selected by the parent. The parent's current
  // modelValue is included so that tags arriving via the props-sync watcher
  // (e.g. a consumer that resolves its botId/node-data asynchronously) are
  // never misclassified as "unknown" and never trigger a spurious createTag
  // call — even when the local `options` cache is still empty because
  // `listTags` hasn't resolved yet.
  //
  // This is the idiomatic guard: it requires no Vue-internals knowledge (no
  // identity checks, no toRaw, no timing flags) and uses the same sorted-join
  // pattern already employed by the props-sync watcher above.
  const knownSet = new Set([...options.value, ...props.modelValue])
  // Items not yet in the fetched options list AND not declared by the parent
  // AND not a CREATE sentinel currently being handled by onItemSelect
  const unknownItems = newVal.filter((v) => !isCreateOption(v) && !knownSet.has(v))

  if (!unknownItems.length) return // normal path — existing event handlers already emit

  // One or more items bypassed onItemSelect (free-text commit path).
  // Run them through createTag before reflecting them in the emitted value.
  let current = [...newVal]
  for (const name of unknownItems) {
    const trimmed = String(name).trim()
    if (!trimmed || !props.botId || !props.creatable) {
      // No botId, empty string, or creatable=false — silently discard rather
      // than calling apiCreateTag. This is what keeps a read-only filter
      // instance from ever introducing a name the catalog doesn't have.
      current = current.filter((v) => v !== name)
      continue
    }
    try {
      const created = await apiCreateTag(props.workspaceId, props.botId, trimmed)
      // Register in options so future suggestions and re-fires see it as known
      if (!options.value.includes(created)) options.value = [...options.value, created]
      // Normalise: API may return a different casing or canonical form
      if (name !== created) current = current.map((v) => (v === name ? created : v))
    } catch {
      // API failure — silently remove the item rather than showing an unpersisted chip
      current = current.filter((v) => v !== name)
    }
  }

  const finalVal = [...new Set(current)]
  // Update internalValue; the watcher will re-fire but unknownItems will be
  // empty (every item is now in options), so it exits immediately.
  internalValue.value = finalVal
  emit('update:modelValue', finalVal)
})

// ── Suggestion filtering ───────────────────────────────────────────────────
/**
 * Called by AutoComplete on every keystroke. Filters `options` by the typed
 * query and appends a CREATE sentinel when the query doesn't exactly match any
 * existing tag (and `botId` is available so creation is possible).
 */
const onComplete = ({ query }) => {
  const q = query.trim()
  const qLow = q.toLowerCase()

  // Exclude already-selected tags from the dropdown
  const selected = new Set(internalValue.value.map((v) => String(v).toLowerCase()))

  const matches = options.value.filter(
    (tag) => !selected.has(tag.toLowerCase()) && (!q || tag.toLowerCase().includes(qLow)),
  )

  const exactMatch = options.value.some((t) => t.toLowerCase() === qLow)

  if (q && !exactMatch && props.botId && props.creatable) {
    // Show existing matches first; the "add" option appears at the bottom
    filteredSuggestions.value = [...matches, `${CREATE_PREFIX}${q}`]
  } else {
    // Read-only (creatable=false) usages, like the Transcripts tag filter,
    // never see the CREATE sentinel — only matches from the existing catalog.
    filteredSuggestions.value = matches
  }
}

// ── Selection handling ─────────────────────────────────────────────────────
/**
 * Handles both "select existing tag" and "create new tag" cases.
 *
 * For the CREATE sentinel: the sentinel is removed from `internalValue`
 * synchronously inside this handler (before Vue flushes the DOM), so the chip
 * never actually renders with the internal prefix string. The real tag name is
 * added after the API call succeeds.
 */
const onItemSelect = async ({ value: selected }) => {
  if (!isCreateOption(selected)) {
    // Existing tag selected — deduplicate and emit
    const unique = [...new Set(internalValue.value)]
    if (unique.length !== internalValue.value.length) internalValue.value = unique
    emit('update:modelValue', [...internalValue.value])
    return
  }

  // Extract the typed name and strip the sentinel synchronously so Vue never
  // renders the raw prefix as a chip.
  const name = selected.slice(CREATE_PREFIX.length).trim()
  internalValue.value = internalValue.value.filter((v) => v !== selected)

  if (!name || !props.botId || !props.creatable) {
    // creatable=false: the sentinel shouldn't be reachable (onComplete never
    // offers it), but guard anyway — treat selection as a no-op, never call
    // apiCreateTag.
    emit('update:modelValue', [...internalValue.value])
    return
  }

  try {
    const created = await apiCreateTag(props.workspaceId, props.botId, name)

    // Register in the local options so it appears in future suggestions
    if (!options.value.includes(created)) {
      options.value = [...options.value, created]
    }

    // Add to the selected chips, guarding against duplicates
    if (!internalValue.value.includes(created)) {
      internalValue.value = [...internalValue.value, created]
    }
  } catch {
    // API failure — tag is silently not added to the model
  }

  emit('update:modelValue', [...internalValue.value])
}

/** Called when the user removes a chip via the × button or Backspace. */
const onItemUnselect = () => {
  emit('update:modelValue', [...internalValue.value])
}
</script>

<template>
  <AutoComplete
    v-model="internalValue"
    :suggestions="filteredSuggestions"
    :placeholder="placeholder"
    multiple
    size="small"
    fluid
    @complete="onComplete"
    @item-select="onItemSelect"
    @item-unselect="onItemUnselect"
  >
    <!--
      Custom option renderer: existing tags display as plain text; the CREATE
      sentinel renders with a "+" icon and shows only the typed name (no prefix).
    -->
    <template #option="{ option }">
      <span
        v-if="isCreateOption(option)"
        class="flex items-center gap-1.5 text-[var(--p-primary-color)]"
      >
        <span class="material-icons !text-[14px]">add_circle_outline</span>
        <span>{{ option.slice(CREATE_PREFIX.length) }}</span>
      </span>
      <span v-else>{{ option }}</span>
    </template>
  </AutoComplete>
</template>
